export const STAFF_ROLE_NAMES = {
  owner: 'owner',
  manager: 'manager',
  tutor: 'tutor',
  salesAgent: 'sales_agent',
} as const;

export type StaffRoleName =
  (typeof STAFF_ROLE_NAMES)[keyof typeof STAFF_ROLE_NAMES];

/** Customer/learner role UUID used when creating clients. */
export const CUSTOMER_ROLE_ID = '4506a6ca-8bb2-4ec9-8fb5-6c081f9cb2ef';

/** Legacy default staff role UUID (tutor) used before role picker existed. */
export const DEFAULT_TUTOR_ROLE_ID = '030df0fc-6b73-4715-9335-b9141018a7f4';

/** Max percent discount a sales agent may apply without manager override. */
export const SALES_AGENT_MAX_DISCOUNT_PERCENT = 10;

/** Max absolute discount (£) a sales agent may apply. */
export const SALES_AGENT_MAX_DISCOUNT_GBP = 150;

/** Release unworked claimed leads back to the pool after this many days. */
export const SALES_AGENT_CLAIM_TTL_DAYS = 7;

/** Users authorised to review and administer annual leave for the organisation. */
export const ANNUAL_LEAVE_REVIEWER_USER_IDS: readonly string[] = [
  '89e5e40b-478c-4438-b7b8-d9a2a7097975',
  '4c730358-f6e9-4303-b634-673d7f5a0c1f',
  '482f9005-1ee2-454b-af47-b80e2775b4d5',
];

const annualLeaveReviewerUserIds = new Set(ANNUAL_LEAVE_REVIEWER_USER_IDS);

export type HasuraClaims = {
  [key: string]: string | undefined;
};

export function getRole(claims: HasuraClaims = {}) {
  return claims['x-hasura-role'] ?? '';
}

export function getUserId(claims: HasuraClaims = {}) {
  return claims['x-hasura-user-id'] ?? '';
}

export function isOwner(claims: HasuraClaims = {}) {
  return getRole(claims) === STAFF_ROLE_NAMES.owner;
}

export function isManager(claims: HasuraClaims = {}) {
  return getRole(claims) === STAFF_ROLE_NAMES.manager;
}

export function isManagerOrOwner(claims: HasuraClaims = {}) {
  const role = getRole(claims);
  return role === STAFF_ROLE_NAMES.manager || role === STAFF_ROLE_NAMES.owner;
}

export function isSalesAgent(claims: HasuraClaims = {}) {
  return getRole(claims) === STAFF_ROLE_NAMES.salesAgent;
}

export function isTutor(claims: HasuraClaims = {}) {
  return getRole(claims) === STAFF_ROLE_NAMES.tutor;
}

/** Manager, owner, tutor, or sales agent - staff who may use managed app shells. */
export function isStaffRole(claims: HasuraClaims = {}) {
  const role = getRole(claims);
  return (
    role === STAFF_ROLE_NAMES.owner ||
    role === STAFF_ROLE_NAMES.manager ||
    role === STAFF_ROLE_NAMES.tutor ||
    role === STAFF_ROLE_NAMES.salesAgent
  );
}

export function canAccessSalesWorkspace(claims: HasuraClaims = {}) {
  return isManagerOrOwner(claims) || isSalesAgent(claims);
}

export function canManageTeam(claims: HasuraClaims = {}) {
  return isManagerOrOwner(claims);
}

export function canReviewAnnualLeave(claims: HasuraClaims = {}) {
  return annualLeaveReviewerUserIds.has(getUserId(claims));
}

/** Mirrors `public.can_record_staff_absence`: owners and allowlisted managers. */
export function canRecordStaffAbsence(claims: HasuraClaims = {}) {
  return isOwner(claims) || (isManager(claims) && canReviewAnnualLeave(claims));
}

export function canViewOrgRevenue(claims: HasuraClaims = {}) {
  return isManagerOrOwner(claims);
}

export function canViewOwnSalesPerformance(claims: HasuraClaims = {}) {
  return isManagerOrOwner(claims) || isSalesAgent(claims);
}

/** Roles allowed on Twilio / Missive engage APIs. */
export const ENGAGE_API_ROLES = [
  STAFF_ROLE_NAMES.owner,
  STAFF_ROLE_NAMES.manager,
  STAFF_ROLE_NAMES.tutor,
  STAFF_ROLE_NAMES.salesAgent,
] as const;

/** Roles denied finance void / uncollectible / bulk campaigns. */
export const FINANCE_ADMIN_ROLES = [
  STAFF_ROLE_NAMES.owner,
  STAFF_ROLE_NAMES.manager,
] as const;

/**
 * Roles allowed to download (export) customer call recordings.
 * Matches finance-admin level: owner and manager only.
 * Tutors and sales agents may play recordings in-app but not download.
 */
export const CALL_RECORDING_DOWNLOAD_ROLES = FINANCE_ADMIN_ROLES;

export function canDownloadCallRecordings(claims: HasuraClaims = {}) {
  return CALL_RECORDING_DOWNLOAD_ROLES.includes(
    getRole(claims) as (typeof CALL_RECORDING_DOWNLOAD_ROLES)[number],
  );
}

export function assertRoleAllowed(
  claims: HasuraClaims,
  allowed: readonly string[],
) {
  const role = getRole(claims);
  if (!allowed.includes(role)) {
    const err = Object.assign(new Error('Forbidden for this role.'), {
      code: 'forbidden',
      statusCode: 403,
    });
    throw err;
  }
}

export const TEAM_INVITE_ROLE_OPTIONS: Array<{
  name: StaffRoleName;
  label: string;
  ownerOnly?: boolean;
}> = [
  { name: STAFF_ROLE_NAMES.tutor, label: 'Tutor' },
  { name: STAFF_ROLE_NAMES.salesAgent, label: 'Sales agent' },
  { name: STAFF_ROLE_NAMES.manager, label: 'Manager', ownerOnly: true },
];
